import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Mail, Phone, Edit, Trash2, User, Upload, Filter, AlertCircle, Camera, X, CheckSquare, Square, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { AthleteAvatar, clearAvatarCache } from '../components/AthleteAvatar';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { athletesAPI, programsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import useSubscription from '../hooks/useSubscription';
import UpgradeBanner from '../components/UpgradeBanner';
import LimitReachedModal from '../components/LimitReachedModal';
import ImportAthletes from './ImportAthletes';

const Athletes = () => {
  const [athletes, setAthletes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProgramFilter, setSelectedProgramFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [athleteToDelete, setAthleteToDelete] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { toast } = useToast();
  
  const {
    isPro,
    programsUsed,
    programsLimit,
    athletesUsed,
    athletesLimit,
    isAtProgramLimit,
    isAtAthleteLimit,
    isApproachingAthleteLimit,
    shouldShowUpgradeBanner,
    canAddAthlete,
    refresh: refreshSubscription
  } = useSubscription();
  
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    emergencyContact: '',
    programs: []
  });
  const [headshot, setHeadshot] = useState(null); // File object for new upload
  const [headshotPreview, setHeadshotPreview] = useState(null); // Preview URL
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Bulk mode state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedAthletes, setSelectedAthletes] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('add');
  const [bulkProgramId, setBulkProgramId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    fetchAthletes();
  }, [selectedProgramFilter]);

  const fetchPrograms = async () => {
    try {
      const programsRes = await programsAPI.getAll();
      setPrograms(programsRes.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast({ title: 'Error loading programs', variant: 'destructive' });
    }
  };

  const fetchAthletes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedProgramFilter && selectedProgramFilter !== 'all') {
        params.program_id = selectedProgramFilter;
      }
      const athletesRes = await athletesAPI.getAll(params);
      setAthletes(athletesRes.data);
      refreshSubscription(); // Refresh subscription to get updated counts
    } catch (error) {
      console.error('Error fetching athletes:', error);
      toast({ title: 'Error loading athletes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    await Promise.all([fetchPrograms(), fetchAthletes()]);
  };

  const handleAddAthleteClick = () => {
    if (!canAddAthlete) {
      setShowLimitModal(true);
      return;
    }
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProgramToggle = (programId) => {
    const updatedPrograms = formData.programs.includes(programId)
      ? formData.programs.filter(id => id !== programId)
      : [...formData.programs, programId];
    setFormData({ ...formData, programs: updatedPrograms });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check limit before creating
    if (!editingAthlete && !canAddAthlete) {
      setShowLimitModal(true);
      setIsDialogOpen(false);
      return;
    }
    
    try {
      let athleteId = editingAthlete?.id;
      if (editingAthlete) {
        await athletesAPI.update(editingAthlete.id, formData);
        toast({ title: 'Athlete updated successfully!' });
      } else {
        const res = await athletesAPI.create(formData);
        athleteId = res.data.id;
        toast({ title: 'Athlete added successfully!' });
      }
      
      // Upload headshot if one was selected
      if (headshot && athleteId) {
        try {
          setUploadingPhoto(true);
          await athletesAPI.uploadHeadshot(athleteId, headshot);
          clearAvatarCache(athleteId);
        } catch (err) {
          console.error('Headshot upload failed:', err);
          toast({ title: 'Athlete saved but photo upload failed', variant: 'destructive' });
        } finally {
          setUploadingPhoto(false);
        }
      }
      
      await fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving athlete:', error);
      // Check if it's a limit error from backend
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('limit')) {
        setShowLimitModal(true);
        setIsDialogOpen(false);
      } else {
        toast({ title: error.response?.data?.detail || 'Error saving athlete', variant: 'destructive' });
      }
    }
  };

  const handleEdit = (athlete) => {
    setEditingAthlete(athlete);
    setFormData(athlete);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (athlete) => {
    setAthleteToDelete(athlete);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!athleteToDelete) return;
    
    try {
      await athletesAPI.delete(athleteToDelete.id);
      toast({ title: 'Athlete removed successfully!' });
      await fetchData();
    } catch (error) {
      console.error('Error deleting athlete:', error);
      toast({ title: 'Error deleting athlete', variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setAthleteToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      dateOfBirth: '',
      email: '',
      phone: '',
      emergencyContact: '',
      programs: []
    });
    setEditingAthlete(null);
    setHeadshot(null);
    if (headshotPreview) {
      URL.revokeObjectURL(headshotPreview);
      setHeadshotPreview(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please select an image file', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image must be under 5MB', variant: 'destructive' });
      return;
    }
    setHeadshot(file);
    setHeadshotPreview(URL.createObjectURL(file));
  };

  const handleRemoveHeadshot = async () => {
    if (editingAthlete?.photo_path) {
      try {
        await athletesAPI.deleteHeadshot(editingAthlete.id);
        clearAvatarCache(editingAthlete.id);
        toast({ title: 'Photo removed' });
        await fetchData();
      } catch (err) {
        console.error('Failed to remove photo:', err);
        toast({ title: 'Failed to remove photo', variant: 'destructive' });
      }
    }
    setHeadshot(null);
    if (headshotPreview) {
      URL.revokeObjectURL(headshotPreview);
      setHeadshotPreview(null);
    }
  };

  // Bulk mode handlers
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedAthletes(new Set());
  };

  const toggleAthleteSelection = (athleteId) => {
    setSelectedAthletes(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) next.delete(athleteId);
      else next.add(athleteId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAthletes.size === filteredAthletes.length) {
      setSelectedAthletes(new Set());
    } else {
      setSelectedAthletes(new Set(filteredAthletes.map(a => a.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkProgramId || selectedAthletes.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await athletesAPI.bulkAssign([...selectedAthletes], bulkProgramId, bulkAction);
      const { updated, program_name, action } = res.data;
      toast({ title: `${updated} athlete${updated !== 1 ? 's' : ''} ${action === 'add' ? 'added to' : 'removed from'} ${program_name}` });
      await fetchData();
      setBulkDialogOpen(false);
      setBulkMode(false);
      setSelectedAthletes(new Set());
      setBulkProgramId('');
    } catch (err) {
      console.error('Bulk assign error:', err);
      toast({ title: err.response?.data?.detail || 'Bulk action failed', variant: 'destructive' });
    } finally {
      setBulkLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const sortByLastName = (a, b) => {
    const lastA = (a.name || '').trim().split(/\s+/).pop().toLowerCase();
    const lastB = (b.name || '').trim().split(/\s+/).pop().toLowerCase();
    return lastA.localeCompare(lastB);
  };

  const filteredAthletes = athletes.filter(athlete =>
    athlete.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort(sortByLastName);

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.name : 'Unknown Program';
  };

  const getProgramColor = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.color : '#0067B9';
  };

  return (
    <div className="space-y-6">
      {/* Limit Reached Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type="athletes"
        currentCount={athletesUsed}
        limit={athletesLimit}
      />

      {/* Upgrade Banner - shows when approaching or at limit */}
      {shouldShowUpgradeBanner && !bannerDismissed && (
        <UpgradeBanner
          programsUsed={programsUsed}
          programsLimit={programsLimit}
          athletesUsed={athletesUsed}
          athletesLimit={athletesLimit}
          isAtProgramLimit={isAtProgramLimit}
          isAtAthleteLimit={isAtAthleteLimit}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{athleteToDelete?.name}</strong> from your athlete roster.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Athlete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Athletes</h1>
          <p className="text-gray-600 mt-1">
            Manage your athlete roster
            {!isPro && (
              <span className="text-sm ml-2 text-gray-500">
                ({athletesUsed}/{athletesLimit} used)
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search athletes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full sm:w-64"
              data-testid="search-athletes-input"
            />
          </div>
          <Select 
            value={selectedProgramFilter} 
            onValueChange={setSelectedProgramFilter}
          >
            <SelectTrigger className="w-full sm:w-48" data-testid="program-filter-select">
              <Filter className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Filter by Program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-all-programs">All Programs</SelectItem>
              {programs.map(program => (
                <SelectItem 
                  key={program.id} 
                  value={program.id}
                  data-testid={`filter-program-${program.id}`}
                >
                  {program.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              style={{ borderColor: '#007A3D', color: '#007A3D' }}
              className="text-sm"
              onClick={async () => {
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/csv/export-athletes`, {
                    credentials: 'include'
                  });
                  if (!response.ok) throw new Error('Export failed');
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `athletes_export_${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  toast({ title: 'Athletes exported successfully!' });
                } catch (error) {
                  console.error('Export error:', error);
                  toast({ title: 'Error exporting athletes', variant: 'destructive' });
                }
              }}
              data-testid="export-athletes-btn"
            >
              <Upload className="h-4 w-4 mr-2 rotate-180" />
              Export CSV
            </Button>
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" style={{ borderColor: '#0067B9', color: '#0067B9' }} className="text-sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import Athletes</DialogTitle>
                </DialogHeader>
                <ImportAthletes 
                  onClose={() => setIsImportDialogOpen(false)}
                  onImportComplete={() => {
                    fetchData();
                    setTimeout(() => setIsImportDialogOpen(false), 2000);
                  }}
                />
              </DialogContent>
            </Dialog>
            <Button
              variant={bulkMode ? "default" : "outline"}
              style={bulkMode ? { backgroundColor: '#0067B9' } : { borderColor: '#0067B9', color: '#0067B9' }}
              onClick={toggleBulkMode}
              className="text-sm"
              data-testid="bulk-mode-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              {bulkMode ? 'Cancel Bulk' : 'Bulk Assign'}
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              if (open && !canAddAthlete) {
                setShowLimitModal(true);
                return;
              }
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                style={{ backgroundColor: '#0067B9' }} 
                className="hover:opacity-90"
                onClick={handleAddAthleteClick}
                data-testid="add-athlete-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Athlete
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingAthlete ? 'Edit Athlete' : 'Add New Athlete'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Headshot Upload Section */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative group">
                    {headshotPreview || editingAthlete?.photo_path ? (
                      <div className="relative">
                        <div className="h-20 w-20 rounded-full overflow-hidden border-2 border-gray-200">
                          <img
                            src={headshotPreview || ''}
                            alt="Headshot"
                            className="h-full w-full object-cover"
                            style={{ display: headshotPreview ? 'block' : 'none' }}
                          />
                          {!headshotPreview && editingAthlete?.photo_path && (
                            <AthleteAvatar athlete={editingAthlete} size="lg" className="h-20 w-20" />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveHeadshot}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                          data-testid="remove-headshot-btn"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="h-20 w-20 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors bg-gray-50"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="headshot-upload-placeholder"
                      >
                        <Camera className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="headshot-file-input"
                    />
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="headshot-camera-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs"
                      data-testid="upload-photo-btn"
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Upload
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cameraInputRef.current?.click()}
                      className="text-xs"
                      data-testid="take-photo-btn"
                    >
                      <Camera className="h-3 w-3 mr-1" />
                      Camera
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Marcus Johnson"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                  {formData.dateOfBirth && (
                    <p className="text-sm text-gray-600 mt-1">
                      Age: {calculateAge(formData.dateOfBirth)} years
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email">
                    Email <span className="text-gray-400 font-normal text-xs">(enter parent email if a minor)</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="athlete@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(345) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContact">Emergency Contact</Label>
                  <Input
                    id="emergencyContact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="(345) 123-4567"
                  />
                </div>
                <div>
                  <Label>Assign to Programs</Label>
                  <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                    {programs.map(program => (
                      <label
                        key={program.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.programs.includes(program.id)}
                          onChange={() => handleProgramToggle(program.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{program.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" style={{ backgroundColor: '#0067B9' }} disabled={uploadingPhoto}>
                    {uploadingPhoto ? 'Uploading...' : editingAthlete ? 'Update' : 'Add'} Athlete
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {bulkMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors"
              data-testid="select-all-btn"
            >
              {selectedAthletes.size === filteredAthletes.length && filteredAthletes.length > 0 ? (
                <CheckSquare className="h-5 w-5 text-blue-600" />
              ) : (
                <Square className="h-5 w-5 text-gray-400" />
              )}
              Select All
            </button>
            <span className="text-sm text-gray-500">
              {selectedAthletes.size} of {filteredAthletes.length} selected
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              style={{ backgroundColor: '#0067B9' }}
              disabled={selectedAthletes.size === 0}
              onClick={() => { setBulkAction('add'); setBulkDialogOpen(true); }}
              data-testid="bulk-add-btn"
            >
              Add to Program
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50"
              disabled={selectedAthletes.size === 0}
              onClick={() => { setBulkAction('remove'); setBulkDialogOpen(true); }}
              data-testid="bulk-remove-btn"
            >
              Remove from Program
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'add' ? 'Add' : 'Remove'} {selectedAthletes.size} Athlete{selectedAthletes.size !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              {bulkAction === 'add'
                ? 'Choose a program to add the selected athletes to:'
                : 'Choose a program to remove the selected athletes from:'}
            </p>
            <Select value={bulkProgramId} onValueChange={setBulkProgramId}>
              <SelectTrigger data-testid="bulk-program-select">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button
              style={bulkAction === 'add' ? { backgroundColor: '#0067B9' } : { backgroundColor: '#dc2626' }}
              disabled={!bulkProgramId || bulkLoading}
              onClick={handleBulkAssign}
              data-testid="bulk-confirm-btn"
            >
              {bulkLoading ? 'Processing...' : bulkAction === 'add' ? 'Add to Program' : 'Remove from Program'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Athletes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading athletes...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAthletes.map(athlete => (
          <Card
            key={athlete.id}
            className={`hover:shadow-lg transition-shadow ${bulkMode && selectedAthletes.has(athlete.id) ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
            onClick={bulkMode ? () => toggleAthleteSelection(athlete.id) : undefined}
            style={bulkMode ? { cursor: 'pointer' } : {}}
          >
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {bulkMode && (
                    <div className="flex-shrink-0" data-testid={`bulk-check-${athlete.id}`}>
                      {selectedAthletes.has(athlete.id) ? (
                        <CheckSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  )}
                  <AthleteAvatar athlete={athlete} size="md" />
                  <div>
                    <h3 className="font-semibold text-gray-800">{athlete.name}</h3>
                    {athlete.dateOfBirth ? (
                      <p className="text-sm text-gray-600">Age {calculateAge(athlete.dateOfBirth)}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No DOB provided</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                {athlete.email && (
                  <div className="flex items-center text-sm">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <a 
                      href={`mailto:${athlete.email}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {athlete.email}
                    </a>
                  </div>
                )}
                {athlete.phone && (
                  <div className="flex items-center text-sm">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <a 
                      href={`tel:${athlete.phone.replace(/[^\d+]/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {athlete.phone}
                    </a>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Programs:</p>
                <div className="flex flex-wrap gap-1">
                  {athlete.programs.filter(pid => programs.some(p => p.id === pid)).length > 0 ? (
                    athlete.programs.filter(pid => programs.some(p => p.id === pid)).map(programId => (
                      <Badge
                        key={programId}
                        variant="outline"
                        className="text-xs"
                        style={{ borderColor: getProgramColor(programId), color: getProgramColor(programId) }}
                      >
                        {getProgramName(programId)}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400">No programs assigned</span>
                  )}
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(athlete)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 hover:text-red-700"
                  onClick={() => handleDeleteClick(athlete)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAthletes.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">
                {searchQuery ? 'No athletes found' : 'No athletes yet'}
              </p>
              <p className="text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Add your first athlete to get started'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
};

export default Athletes;